import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import QuestionnaireGeneralDataCard from './QuestionnaireGeneralDataCard';
import { initialState, reducer } from 'features/questionnaires/reducers/generalData/containerReducer';
import { useValidation } from '@totalsoft/pure-validations-react';
import { questionnaireValidator } from 'features/questionnaires/validators';
import { useTranslation } from 'react-i18next';
import { QUESTIONNAIRE_SAVE, EVALUATE_QUESTIONNAIRE_FORMULA } from 'features/questionnaires/mutations';
import { QUESTIONNAIRE_SAVE_SUBSCRIPTION } from 'features/questionnaires/subscriptions';
import { useApolloClient } from '@apollo/client';
import { useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';

const QuestionnaireGeneralDataContainer = ({ questionnaireId, data, onChangeQuestionnaireTitle, onRedirect, isReadOnly }) => {
  const addToast = useToast();
  const { t } = useTranslation();
  const [localState, dispatch] = useReducer(reducer, initialState);
  const [questionnaireValidation, validateQuestionnaire] = useValidation(questionnaireValidator);
  const client = useApolloClient();

  const [QuestionnaireSave] = useMutation(QUESTIONNAIRE_SAVE, {
    skip: !localState?.questionnaire,
    variables: { input: localState?.questionnaire }
  });

  const [EvaluateQuestionnaireFormula] = useMutation(EVALUATE_QUESTIONNAIRE_FORMULA, {
    skip: !localState?.questionnaire,
    variables: { input: localState?.questionnaire }
  });

  useSubscription(QUESTIONNAIRE_SAVE_SUBSCRIPTION, {
    onData: data => {
      if (data?.data?.data?.questionnaireUpdated?.questionnaire?.isSaved) {
        client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'questionnaires' });
        client.cache.evict({
          id: `Questionnaire:{"questionnaireId":${data.data.data.questionnaireUpdated.questionnaire.questionnaireId}}`
        });
        client.cache.gc();
        dispatch({ type: 'OnHeaderSaved', questionnaireId: questionnaireId });
        addToast(t('Questionnaire.QuestionnaireSaved'), 'success');
        if (!questionnaireId) {
          onRedirect(data?.data.data?.questionnaireUpdated?.questionnaire?.questionnaireId);
        }
      } else {
        addToast(t('Questionnaire.QuestionnaireFilterFormulaNotOk'), 'error');
      }
    }
  });

  const handleSave = () => {
    if (!validateQuestionnaire(localState.questionnaire)) return;
    EvaluateQuestionnaireFormula().then(({ data }) => {
      data?.evaluateQuestionnaireFormula ? QuestionnaireSave() : addToast(t('Questionnaire.QuestionnaireFilterFormulaNotOk'), 'error');
    });
  };

  const handlePropertyChanged = (propertyName, value) => {
    dispatch({ type: 'OnHeaderPropertyChanged', propertyName, value });
  };

  useEffect(() => {
    validateQuestionnaire(localState.questionnaire);
  }, [localState.questionnaire, validateQuestionnaire]);

  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data });
  }, [data]);

  useEffect(() => {
    if (data?.name) {
      onChangeQuestionnaireTitle(data.name);
    }
  }, [data, onChangeQuestionnaireTitle]);

  return (
    <QuestionnaireGeneralDataCard
      questionnaire={localState.questionnaire}
      onSave={handleSave}
      validation={questionnaireValidation}
      isReadOnly={isReadOnly}
      onPropertyChanged={handlePropertyChanged}
    />
  );
};

QuestionnaireGeneralDataContainer.propTypes = {
  questionnaireId: PropTypes.number,
  data: PropTypes.object,
  onChangeQuestionnaireTitle: PropTypes.func,
  onRedirect: PropTypes.func,
  isReadOnly: PropTypes.bool
};

export default QuestionnaireGeneralDataContainer;
